"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Typography,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Button,
  Box,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { LocationOn, Badge, School, CheckCircle } from "@mui/icons-material";
// import {
//   getOrientationTasks,
//   completeOrientationTask,
//    submitOrientationCompletion,
// } from "../../../Services/OnboardingService2"
import styles from "./OnboardingOrientation.module.css";
import {
  completeOrientationTask,
  getEmployeeOrientationTasks,
  getOrientationTasks,
  submitOrientationCompletion,
} from "../../api/OnboardingService2";

interface OnboardingOrientationProps {
  employeeId: string;
  isCompleted: boolean;
  onComplete: () => void;
}

interface OrientationTask {
  id: string;
  title: string;
  description: string;
  type: "physical" | "virtual" | "documentation";
  location?: string;
  responsible: string;
  completed: boolean;
  completedDate?: string;
  notes?: string;
  required: boolean;
  status: "0" | "1"; // 1 = required, 2 = optional
}

const OnboardingOrientation: React.FC<OnboardingOrientationProps> = ({
  employeeId,
  isCompleted,
  onComplete,
}) => {
  const [tasks, setTasks] = useState<OrientationTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<OrientationTask | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    fetchOrientationTasks();
  }, [employeeId]);

  const fetchOrientationTasks = async () => {
    try {
      setLoading(true);
      const data = await getEmployeeOrientationTasks();

      setTasks(data);
    } catch (err) {
      setError("Failed to load orientation tasks");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = (task: OrientationTask) => {
    if (!task.status && !isCompleted) {
      setSelectedTask(task);
      setNotes(task.notes || "");
      setDialogOpen(true);
    }
  };

  const handleTaskComplete = async () => {
    if (!selectedTask) return;
    console.log("completing task", employeeId, selectedTask.task_id, notes);
    // return

    try {
      setSubmitting(true);
      await completeOrientationTask(employeeId, selectedTask.task_id, notes);

      // Update local state
      setTasks(
        tasks.map((task) =>
          task.id === selectedTask.id
            ? {
                ...task,
                completed: true,
                completedDate: new Date().toISOString(),
                notes,
              }
            : task
        )
      );

      setDialogOpen(false);
      setSelectedTask(null);
      setNotes("");
    } catch (err) {
      setError("Failed to complete task");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitCompletion = async () => {
    try {
      setSubmitting(true);
      //  await submitOrientationCompletion(employeeId)
      onComplete();
    } catch (err) {
      setError("Failed to submit orientation completion");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const getTaskIcon = (type: string) => {
    switch (type) {
      case "physical":
        return <LocationOn />;
      case "documentation":
        return <Badge />;
      default:
        return <School />;
    }
  };

  const getCompletedTasksCount = () => {
    return tasks.filter((task) => task.status == "1").length;
  };

  const getRequiredTasksCount = () => {
    return tasks.filter((task) => task.status === "1").length;
  };

  const canComplete = () => {
    // const requiredTasks = tasks.filter((task) => task.status == "1")
    // console.log("required tasks", requiredTasks)
    const canComplete = tasks.every((task) => task.status == "1");
    if (canComplete) return onComplete();
  };

  // console.log("completed tasks", canComplete(), tasks)
  if (loading) {
    return (
      <Card className={styles.card}>
        <CardContent>
          <Typography>Loading orientation tasks...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className={styles.error}>
        {error}
      </Alert>
    );
  }

  const completedCount = getCompletedTasksCount();
  const requiredCount = getRequiredTasksCount();

  return (
    <div className={styles.container}>
      <Card className={styles.progressCard}>
        <CardHeader
          title="Orientation Progress"
          subheader={`${completedCount}/${tasks.length} tasks completed`}
          action={
            isCompleted ? (
              <Chip label="Completed" color="success" icon={<CheckCircle />} />
            ) : (
              <Chip
                label={`${
                  tasks.filter((t) => t.required && t.status).length
                }/${requiredCount} Required`}
                color={canComplete() ? "success" : "primary"}
              />
            )
          }
        />
      </Card>

      {isCompleted && (
        <Alert severity="success" className={styles.completedAlert}>
          <Typography variant="h6">Orientation Completed!</Typography>
          <Typography>
            You have successfully completed all orientation requirements.
          </Typography>
        </Alert>
      )}

      <Card className={styles.tasksCard}>
        <CardHeader
          title="Orientation Tasks"
          subheader="Complete the following orientation and clearance tasks"
        />
        <CardContent>
          <List className={styles.tasksList}>
            {tasks.map((task) => (
              <ListItem
                key={task.id}
                button={!task.status && !isCompleted}
                onClick={() => handleTaskClick(task)}
                className={`${styles.taskItem} ${
                  task.status ? styles.completed : ""
                }`}
              >
                <ListItemIcon>
                  {task.status ? (
                    <CheckCircle color="success" />
                  ) : (
                    getTaskIcon(task.type)
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box className={styles.taskHeader}>
                      <Typography variant="subtitle1">{task.title}</Typography>
                      {task.required && (
                        <Chip label="Required" size="small" color="error" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {task.description}
                      </Typography>
                      {task.location && (
                        <Typography
                          variant="caption"
                          className={styles.location}
                        >
                          Location: {task.location}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        className={styles.responsible}
                      >
                        Responsible: {task.responsible}
                      </Typography>
                      {/* {task.status && task.completedDate && (
                        <Typography variant="caption" className={styles.completedDate}>
                          Completed: {new Date(task.completedDate).toLocaleDateString()} tsk : { task.status}
                        </Typography>
                      )} */}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Checkbox
                    checked={task.status == "1"}
                    disabled={isCompleted}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {!isCompleted && canComplete() && (
            <Box className={styles.submitSection}>
              <Alert severity="success" className={styles.readyAlert}>
                All required orientation tasks have been completed!
              </Alert>
            </Box>
          )}
          {!isCompleted && !canComplete() && (
            <Box className={styles.submitSection}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSubmitCompletion}
                disabled={submitting}
                className={styles.submitButton}
              >
                {submitting ? "Submitting..." : "Complete Orientation"}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Complete Task: {selectedTask?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" className={styles.dialogDescription}>
            {selectedTask?.description}
          </Typography>
          {selectedTask?.location && (
            <Typography variant="body2" className={styles.dialogLocation}>
              <LocationOn fontSize="small" /> {selectedTask.location}
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about completing this task..."
            className={styles.notesField}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleTaskComplete}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? "Completing..." : "Mark Complete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OnboardingOrientation;
